import { useCallback, useMemo } from "react"

import * as turf from "@turf/turf"

import { CatalogItemDetailsForm } from "@l2r-front/l2r-common"
import { sanitizeData } from "@l2r-front/l2r-geodata"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { useNetworkReferential } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { EQUIPMENT_CONDITION_GOOD, EQUIPMENT_CONDITIONS, EQUIPMENT_CONDITIONS_VALUES } from "../../constants/equipmentConditions"
import { useEquipmentCreate } from "../../hooks/mutations/useEquipmentCreate"
import { useEquipmentProject } from "../../hooks/queries/equipment/useEquipmentProject"
import { useEquipmentTypes } from "../../hooks/queries/equipment/useEquipmentsType"

import * as Styled from "./EquipmentAddForm.styled"

export const EquipmentAddFormPost = (props) => {

    const {
        className,
        close,
        startingPosition,
        ...otherProps
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const navigate = useNavigateWithSearchParams()

    const { data: project } = useEquipmentProject()
    const { data: equipmentTypes, isLoading: isLoadingTypes, isError: isErrorTypes } = useEquipmentTypes()

    const existingTags = useMemo(() => {
        if (project?.tags) {
            return project.tags.sort()
        }

        return []
    }, [project])

    const conditions = useMemo(() => {
        return EQUIPMENT_CONDITIONS.filter(condition => condition > 0)
            .map(condition => ({
                value: condition,
                label: t(I18N_NAMESPACE, EQUIPMENT_CONDITIONS_VALUES[condition].label),
            }))
    }, [t])

    const purchasedEquipmentTypes = useMemo(() => {
        const filterPurchasedEquipment = (equipmentTypes) => {
            return equipmentTypes
                .filter(equipmentType => equipmentType.is_purchased)
                .map(equipmentType => ({
                    ...equipmentType,
                    children: filterPurchasedEquipment(equipmentType.children),
                }))
        }

        return filterPurchasedEquipment(equipmentTypes)
    }, [equipmentTypes])

    const initialValues = useMemo(() => ({
        horizontal_sign_type: undefined,
        condition: EQUIPMENT_CONDITION_GOOD,
        content: "",
        tags: [],
    }), [])

    const { data: referential, isLoading: isLoadingReferential, isError: isErrorReferential } = useNetworkReferential()
    const sanitizedReferential = useMemo(() => {
        return sanitizeData(referential)
    }, [referential])

    const mutation = useEquipmentCreate({}, {
        onSuccess: (data) => navigate(`../${data.linear_location.road}/${data.uuid}`),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.equipmentAddForm.snackbar.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.equipmentAddForm.snackbar.success"),
    })

    const onSubmit = useCallback(async values => {
        const {
            position,
            road,
            ...equipmentParams
        } = values

        const linearLocation = {
            typeid: 0,
            road: road.road,
            start: road.start,
            end: road.start + 1,
        }
        const point = turf.point([position.longitude, position.latitude])

        const params = {
            ...equipmentParams,
            geometry: point.geometry,
            linear_location: linearLocation,
            project: project.uuid,
        }

        mutation.mutate(params)

        close(false)
    }, [close, mutation, project])

    if (isLoadingTypes || isLoadingReferential) {
        return <Styled.FormSkeleton className={className} />
    }

    if (isErrorTypes || isErrorReferential) {
        return <Styled.ErrorMessage>{t(I18N_NAMESPACE, "containers.equipmentDetailsForm.error")}</Styled.ErrorMessage>
    }

    return <CatalogItemDetailsForm
        {...otherProps}
        forceTextContent
        disableMaxDistanceCheck
        initialValues={initialValues}
        catalogTypes={purchasedEquipmentTypes}
        typeField="equipment_type"
        typeFieldLabel={t(I18N_NAMESPACE, "containers.equipmentDetailsForm.type") + "*"}
        conditions={conditions}
        existingTags={existingTags}
        referential={sanitizedReferential}
        startingPosition={startingPosition}
        onSubmit={onSubmit}
        onCancel={close}
    />
}

EquipmentAddFormPost.propTypes = {
    close: PropTypes.func.isRequired,
    startingPosition: PropTypes.shape({
        longitude: PropTypes.number,
        latitude: PropTypes.number,
    }).isRequired,
    startingRoad: PropTypes.string,
}